// FilterChipBar — search input + multi-select chips per brand skill
// §9.1. Chips: Status / Owner / RAG / SOW / Milestone / Due open
// popovers; Include archived is a binary toggle. Active multi-select
// chips show a count badge; the active palette is bg-brand-50 /
// border-brand-400. The SOW chip is searchable: a wide popover with its
// own SOW search above SOW_SEARCH_THRESHOLD options (Unattached pinned).
//
// Mine-only is deferred — the backend doesn't expose an "owned by
// current user" relation today.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  STATUS_FILTER_LABELS,
  STATUS_FILTER_VALUES,
  type StatusFilterValue,
} from "./deliverableStatusFilter";
import type {
  DeliverableOwner,
  Milestone,
  RagRating,
  Sow,
} from "../../../../types";
import DebouncedSearchInput from "../shared/DebouncedSearchInput";
import {
  SOW_SEARCH_THRESHOLD,
  matchesSowSearch,
  compareSowNumbers,
} from "../shared/sowPickerSearch";

/** Synthetic token in `filters.sow` matching items with `sowId == null`.
 *  Backend SOW ids carry a `tracking-`/ULID-prefixed shape so the literal
 *  `"unattached"` cannot collide. See applyChipFilters tests for the
 *  collision-impossibility lock-in. */
export const UNATTACHED_TOKEN = "unattached" as const;
export type SowFilterValue = string;

/** Synthetic token in `filters.milestone` matching items with
 *  `milestoneId == null`. Backend milestone ids carry the same
 *  `tracking-`/ULID-prefixed shape as SOW ids so the literal
 *  `"unlinked"` cannot collide with a real id. */
export const UNLINKED_MILESTONE_TOKEN = "unlinked" as const;
export type MilestoneFilterValue = string;

export type { StatusFilterValue } from "./deliverableStatusFilter";
export type RagFilterValue = RagRating | "not-set";
export type DueFilterValue =
  | "overdue"
  | "this-week"
  | "next-30-days"
  | "future";

export interface FilterChipBarFilters {
  status: StatusFilterValue[];
  owner: DeliverableOwner[];
  rag: RagFilterValue[];
  due: DueFilterValue[];
  /** SOW attachment filter. Carries SOW ids and/or the synthetic
   *  `UNATTACHED_TOKEN` for unattached deliverables. Empty array =
   *  no SOW filter active. */
  sow: SowFilterValue[];
  /** Milestone-link filter. Carries milestone ids and/or the
   *  synthetic `UNLINKED_MILESTONE_TOKEN` for deliverables with no
   *  milestone link. Empty array = no milestone filter active. */
  milestone: MilestoneFilterValue[];
  includeArchived: boolean;
  /** Quick-filter toggle: when true, keeps only deliverables with
   *  failedAcceptanceCount > 0. Surfaces the cohort a contract
   *  manager most wants to triage post-acceptance-failure. */
  hasFailedAcceptance: boolean;
  /** Quick-filter toggle: when true, hides deliverables whose derived
   *  status is `accepted` (everything still open on acceptance — incl.
   *  Cond. accepted, Closed, Rejected, Overdue/Late — remains). */
  notAccepted: boolean;
}

export interface FilterChipBarProps {
  filters: FilterChipBarFilters;
  onFiltersChange: (next: FilterChipBarFilters) => void;
  search: string;
  onSearchChange: (next: string) => void;
  /** SOWs on the parent contract — drives the SOW chip popover.
   *  Archived/cancelled excluded from options (consistent with the
   *  form picker). When undefined or empty the chip still renders
   *  (the synthetic Unattached option is always available). */
  sows?: readonly Sow[];
  /** Milestones on the parent contract — drives the Milestone chip
   *  popover. Archived milestones are excluded from options
   *  (consistent with the form picker). When undefined or empty the
   *  chip still renders (the synthetic Unlinked option is always
   *  available so users can isolate deliverables without a
   *  milestone link). */
  milestones?: readonly Milestone[];
  /** Bump to force the local search-input state to resync with the
   *  `search` prop AND drop any in-flight debounce. Needed because the
   *  user can hit Clear-all *before* the typing-debounce flushes — at
   *  which point `search` is still the (empty) committed prop value, so
   *  a `[search]` useEffect won't fire to clear the local state. The
   *  parent increments this counter on every Clear-all to force the
   *  reset regardless of whether the prop value visibly changed. */
  resetCounter?: number;
  /** Hide the SOW chip when the surface is already SOW-scoped via the
   *  scope selector. The underlying parser + applyChipFilters predicate
   *  stay intact (they no-op against an empty `filters.sow`), so toggling
   *  the prop only controls visibility. Defaults to true to preserve
   *  legacy callers. */
  showSowChip?: boolean;
}

interface OptionDef<V extends string> {
  value: V;
  label: string;
}

// Derived from the canonical status vocabulary so the chip options can
// never drift from the URL-state allow-list (both read STATUS_FILTER_*).
const STATUS_OPTIONS: OptionDef<StatusFilterValue>[] = STATUS_FILTER_VALUES.map(
  (value) => ({ value, label: STATUS_FILTER_LABELS[value] }),
);

const OWNER_OPTIONS: OptionDef<DeliverableOwner>[] = [
  { value: "supplier", label: "Supplier" },
  { value: "customer", label: "Customer" },
  { value: "joint", label: "Joint" },
];

const RAG_OPTIONS: OptionDef<RagFilterValue>[] = [
  { value: "red", label: "Red" },
  { value: "amber", label: "Amber" },
  { value: "green", label: "Green" },
  { value: "not-set", label: "Not set" },
];

const DUE_OPTIONS: OptionDef<DueFilterValue>[] = [
  { value: "overdue", label: "Overdue" },
  { value: "this-week", label: "This week" },
  { value: "next-30-days", label: "Next 30 days" },
  { value: "future", label: "Future" },
];

// N4: focus-visible (not focus) so mouse users don't see the ring on
// click while keyboard users still do.
const CHIP_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";

const CHIP_INACTIVE =
  "border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";

const CHIP_ACTIVE =
  "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-300";

// Popover height capped + internal scroll so a chip near the viewport
// bottom doesn't overflow off-screen. Simpler than flip-to-top because
// it doesn't require viewport-rect math.
//
// Width: absolutely-positioned div without an explicit width sizes to
// content (shrink-to-fit). min-w sets a sensible floor; max-w-md caps
// it so long option labels (long milestone names, SOW lines) can grow
// the popover without stretching it past the chip bar visually.
const POPOVER_CLASS =
  "absolute z-20 mt-1 min-w-[200px] max-w-md max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800";

// Searchable chips (SOW): fixed comfortable width instead of
// shrink-to-fit — prod SOW names run long and the search input needs a
// stable width to sit in. Viewport-capped so it never overflows small
// screens.
const SEARCHABLE_POPOVER_CLASS =
  "absolute z-20 mt-1 w-96 max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800";

interface MultiSelectChipProps<V extends string> {
  chipKey: "status" | "owner" | "rag" | "due" | "sow" | "milestone";
  label: string;
  options: OptionDef<V>[];
  selected: V[];
  onToggle: (next: V[]) => void;
  /** Opt-in popover search for long option lists (the SOW chip). The
   *  input appears only above SOW_SEARCH_THRESHOLD non-pinned options;
   *  the popover also switches to a fixed comfortable width so long
   *  labels don't wrap into a sliver. */
  searchable?: boolean;
  /** Options exempt from the search filter, rendered pinned at the top
   *  of the list (synthetic modes like Unattached — not real options a
   *  user would search for). */
  pinnedValues?: readonly V[];
}

function MultiSelectChip<V extends string>({
  chipKey,
  label,
  options,
  selected,
  onToggle,
  searchable = false,
  pinnedValues = [],
}: MultiSelectChipProps<V>) {
  const [open, setOpen] = useState(false);
  const [optionSearch, setOptionSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const isActive = selected.length > 0;

  const pinnedOptions = options.filter((o) => pinnedValues.includes(o.value));
  const searchTargets = options.filter(
    (o) => !pinnedValues.includes(o.value),
  );
  const searchActive =
    searchable && searchTargets.length > SOW_SEARCH_THRESHOLD;
  const visibleOptions = searchActive
    ? [
        ...pinnedOptions,
        ...searchTargets.filter((o) =>
          matchesSowSearch(o.label, optionSearch),
        ),
      ]
    : options;

  // Reopening starts unfiltered — a stale query from the last visit
  // would silently hide options. Render-time prev-value guard (not an
  // effect) per the react-hooks set-state-in-effect rule.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) setOptionSearch("");
  }

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const handleToggleOption = (value: V) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onToggle(next);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
      >
        <span>{label}</span>
        {isActive && (
          <span className="rounded bg-white px-1 text-[10px] text-brand-600 dark:bg-brand-900 dark:text-brand-300">
            {selected.length}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid={`filter-popover-${chipKey}`}
          role="listbox"
          aria-multiselectable="true"
          className={searchable ? SEARCHABLE_POPOVER_CLASS : POPOVER_CLASS}
        >
          {searchActive && (
            <input
              type="search"
              className="input mb-2 w-full"
              placeholder="Search SOWs…"
              aria-label="Search SOWs"
              value={optionSearch}
              onChange={(e) => setOptionSearch(e.target.value)}
              data-testid={`filter-${chipKey}-search`}
            />
          )}
          <ul className="space-y-1">
            {visibleOptions.map((opt) => {
              const id = `filter-${chipKey}-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={() => handleToggleOption(opt.value)}
                      className="accent-brand-600"
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          {searchActive && visibleOptions.length === pinnedOptions.length && (
            <p
              className="px-2 py-1 text-caption text-slate-500 dark:text-slate-400"
              data-testid={`filter-${chipKey}-search-empty`}
            >
              No SOWs match &ldquo;{optionSearch.trim()}&rdquo;.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

interface BinaryChipProps {
  label: string;
  active: boolean;
  onToggle: () => void;
}

function BinaryChip({ label, active, onToggle }: BinaryChipProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={`${CHIP_BASE} ${active ? CHIP_ACTIVE : CHIP_INACTIVE}`}
    >
      {label}
    </button>
  );
}

export default function FilterChipBar({
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  sows,
  milestones,
  resetCounter,
  showSowChip = true,
}: FilterChipBarProps) {
  const update = <K extends keyof FilterChipBarFilters>(
    key: K,
    value: FilterChipBarFilters[K],
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  // SOW chip options: Unattached synthetic first, then active SOWs
  // (archived/cancelled excluded — consistent with the form picker).
  // Sorted by SOW number for predictable scanning.
  const sowOptions = useMemo<OptionDef<SowFilterValue>[]>(() => {
    const opts: OptionDef<SowFilterValue>[] = [
      { value: UNATTACHED_TOKEN, label: "Unattached" },
    ];
    if (!sows) return opts;
    const active = [...sows]
      .filter((s) => !s.archivedAt && s.status !== "cancelled")
      .sort((a, b) => compareSowNumbers(a.number, b.number));
    for (const s of active) {
      opts.push({ value: s.id, label: `${s.number} · ${s.name}` });
    }
    return opts;
  }, [sows]);

  // Milestone chip options: Unlinked synthetic first, then active
  // milestones (archived excluded — consistent with the form
  // picker). Sorted by milestoneNumber when present, then by name,
  // so the picker reads in the same order users see elsewhere.
  const milestoneOptions = useMemo<OptionDef<MilestoneFilterValue>[]>(() => {
    const opts: OptionDef<MilestoneFilterValue>[] = [
      { value: UNLINKED_MILESTONE_TOKEN, label: "Unlinked" },
    ];
    if (!milestones) return opts;
    const active = [...milestones]
      .filter((m) => !m.archivedAt)
      .sort((a, b) => {
        const an = a.milestoneNumber ?? Number.POSITIVE_INFINITY;
        const bn = b.milestoneNumber ?? Number.POSITIVE_INFINITY;
        if (an !== bn) return an - bn;
        return a.name.localeCompare(b.name);
      });
    for (const m of active) {
      const numPrefix =
        typeof m.milestoneNumber === "number"
          ? `${m.milestoneNumber}${m.milestoneSuffix ?? ""} · `
          : "";
      opts.push({ value: m.id, label: `${numPrefix}${m.name}` });
    }
    return opts;
  }, [milestones]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DebouncedSearchInput
        value={search}
        onDebouncedChange={onSearchChange}
        resetCounter={resetCounter}
        ariaLabel="Search deliverables"
        placeholder="Search… (-exclude)"
        title={'Words are ANDed; "quote a phrase"; prefix -exclude to omit.'}
      />

      <MultiSelectChip
        chipKey="status"
        label="Status"
        options={STATUS_OPTIONS}
        selected={filters.status}
        onToggle={(next) => update("status", next)}
      />
      <MultiSelectChip
        chipKey="owner"
        label="Owner"
        options={OWNER_OPTIONS}
        selected={filters.owner}
        onToggle={(next) => update("owner", next)}
      />
      <MultiSelectChip
        chipKey="rag"
        label="RAG"
        options={RAG_OPTIONS}
        selected={filters.rag}
        onToggle={(next) => update("rag", next)}
      />
      {showSowChip && (
        <MultiSelectChip
          chipKey="sow"
          label="SOW"
          options={sowOptions}
          selected={filters.sow}
          onToggle={(next) => update("sow", next)}
          searchable
          pinnedValues={[UNATTACHED_TOKEN]}
        />
      )}
      <MultiSelectChip
        chipKey="milestone"
        label="Milestone"
        options={milestoneOptions}
        selected={filters.milestone}
        onToggle={(next) => update("milestone", next)}
      />
      <MultiSelectChip
        chipKey="due"
        label="Due"
        options={DUE_OPTIONS}
        selected={filters.due}
        onToggle={(next) => update("due", next)}
      />
      <BinaryChip
        label="Include archived"
        active={filters.includeArchived}
        onToggle={() => update("includeArchived", !filters.includeArchived)}
      />
      <span
        aria-hidden="true"
        data-testid="filter-chip-divider"
        className="mx-1 h-5 w-px bg-slate-300 dark:bg-slate-600"
      />
      <BinaryChip
        label="Has failed acceptance"
        active={filters.hasFailedAcceptance}
        onToggle={() =>
          update("hasFailedAcceptance", !filters.hasFailedAcceptance)
        }
      />
      <BinaryChip
        label="Not accepted"
        active={filters.notAccepted}
        onToggle={() => update("notAccepted", !filters.notAccepted)}
      />
    </div>
  );
}
